import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEur, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, ClipboardCheck, ChevronLeft, ChevronRight } from "lucide-react";

export default function OpenConfirmations() {
  const { t, canWrite } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    api.get(`/orders?status=Entered&sort=order_date&dir=asc&page=${page}&page_size=50`)
      .then((r) => setData(r.data)).catch(() => {});
  }, [page]);
  useEffect(load, [load]);

  const confirm = async (e, o) => {
    e.stopPropagation();
    try {
      await api.post(`/orders/${o.id}/status`, { status: "Confirmed" });
      toast.success(`${o.order_number} ${t("ord_confirmed")} ✓`);
      load();
    } catch (err) { toast.error(errDetail(err)); }
  };

  return (
    <div className="space-y-4" data-testid="open-confirmations-page">
      <div className="flex items-center gap-3">
        <ClipboardCheck className="h-7 w-7 text-[#F5A623]" />
        <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("open_confirmations")}</h1>
        <span className="text-sm text-gray-400 font-mono mt-1" data-testid="openconf-count">({data.total.toLocaleString("de-DE")})</span>
      </div>
      <p className="text-sm text-gray-500">{t("ord_openconf_desc")}</p>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="px-4 py-2.5 font-semibold">{t("ord_order_number")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("ord_customer")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("ord_po")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("ord_date")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("ord_requested")}</th>
                <th className="px-4 py-2.5 font-semibold text-right">{t("ord_total")}</th>
                {canWrite && <th className="px-4 py-2.5" />}
              </tr>
            </thead>
            <tbody>
              {data.items.map((o) => (
                <tr key={o.id} data-testid="openconf-row" onClick={() => navigate(`/orders/${o.id}`)}
                  className="border-b border-gray-100 hover:bg-amber-50/40 cursor-pointer transition-colors duration-150">
                  <td className="px-4 py-2.5 font-mono font-medium text-[#1B2A4A]">{o.order_number}</td>
                  <td className="px-4 py-2.5">{o.account_name}</td>
                  <td className="px-4 py-2.5 font-mono text-gray-500">{o.po_number || "–"}</td>
                  <td className="px-4 py-2.5">{fmtDate(o.order_date)}</td>
                  <td className="px-4 py-2.5">{fmtDate(o.requested_date)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmtEur(o.total, o.currency)}</td>
                  {canWrite && (
                    <td className="px-4 py-2.5 text-right">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7"
                        onClick={(e) => confirm(e, o)} data-testid={`openconf-confirm-${o.order_number}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{t("ord_confirm")}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400" data-testid="openconf-empty">{t("ord_no_open")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {data.pages > 1 && (
          <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-100">
            <div className="flex-1" />
            <span className="text-xs text-gray-500">{t("ord_page")} {data.page || page} / {data.pages}</span>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );
}
